'use client';

import { Skeleton } from '@repo/ui/components/skeleton';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FaqItem } from '@/lib/product-page-settings';

interface ProductFaqCarouselProps {
	eyebrow?: string;
	heading?: string;
	items: FaqItem[];
}

function FaqCard({ item }: { item: FaqItem }) {
	return (
		<div className="flex h-full flex-col rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
			<h3 className="text-sm font-bold uppercase tracking-wide text-neutral-900">
				{item.question}
			</h3>
			<p className="mt-3 text-sm leading-relaxed text-neutral-600">{item.answer}</p>
		</div>
	);
}

function FaqHeader({ eyebrow, heading }: { eyebrow?: string; heading?: string }) {
	return (
		<div>
			{eyebrow && (
				<p className="text-xs font-semibold uppercase tracking-widest text-brand-accent">
					{eyebrow}
				</p>
			)}
			{heading && (
				<h2 className="mt-1 text-2xl font-black uppercase tracking-tight text-neutral-900 md:text-3xl">
					{heading}
				</h2>
			)}
		</div>
	);
}

/**
 * Defers hydration of the interactive embla carousel (prev/next/drag) until the
 * section scrolls near the viewport. Until then we render a no-JS CSS scroll-snap
 * version so the below-the-fold FAQ block costs nothing on initial load.
 */
export function ProductFaqCarousel({ eyebrow, heading, items }: ProductFaqCarouselProps) {
	const containerRef = useRef<HTMLElement | null>(null);
	const [mountCarousel, setMountCarousel] = useState(false);

	useEffect(() => {
		if (mountCarousel) return;
		const node = containerRef.current;
		if (!node) return;
		if (typeof IntersectionObserver === 'undefined') {
			setMountCarousel(true);
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setMountCarousel(true);
					observer.disconnect();
				}
			},
			{ rootMargin: '200px' },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [mountCarousel]);

	if (items.length === 0) return null;

	return (
		<section ref={containerRef} className="bg-neutral-50 py-16">
			{mountCarousel ? (
				<ProductFaqEmbla eyebrow={eyebrow} heading={heading} items={items} />
			) : (
				<div className="container-page">
					<div className="flex items-end justify-between">
						<FaqHeader eyebrow={eyebrow} heading={heading} />
					</div>
					<div className="-ml-4 mt-8 flex snap-x snap-mandatory overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
						{items.map((item) => (
							<div
								key={item.question}
								className="min-w-0 shrink-0 basis-[85%] snap-start pl-4 sm:basis-[45%] md:basis-1/3 lg:basis-1/4"
							>
								<FaqCard item={item} />
							</div>
						))}
					</div>
				</div>
			)}
		</section>
	);
}

function ProductFaqEmbla({ eyebrow, heading, items }: ProductFaqCarouselProps) {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'start',
		dragFree: true,
		loop: false,
		containScroll: 'trimSnaps',
		slidesToScroll: 1,
		duration: 25,
	});

	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [canScrollNext, setCanScrollNext] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const [totalPages, setTotalPages] = useState(1);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setCanScrollPrev(emblaApi.canScrollPrev());
		setCanScrollNext(emblaApi.canScrollNext());

		const snapCount = emblaApi.scrollSnapList().length;
		const selectedSnap = emblaApi.selectedScrollSnap();
		setTotalPages(snapCount);
		setActiveIndex(selectedSnap);
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		emblaApi.on('select', onSelect);
		emblaApi.on('reInit', onSelect);
		return () => {
			emblaApi.off('select', onSelect);
			emblaApi.off('reInit', onSelect);
		};
	}, [emblaApi, onSelect]);

	return (
		<div className="container-page">
			<div className="flex items-end justify-between">
				<FaqHeader eyebrow={eyebrow} heading={heading} />
				{totalPages > 1 && (
					<div className="flex items-center gap-2 text-sm text-neutral-500">
						<button
							type="button"
							onClick={scrollPrev}
							disabled={!canScrollPrev}
							className="transition-colors hover:text-neutral-900 disabled:opacity-30"
							aria-label="Previous questions"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
								fill="currentColor"
								className="h-4 w-4"
								aria-hidden="true"
							>
								<title>Previous</title>
								<path
									fillRule="evenodd"
									d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
						<span>
							{activeIndex + 1} / {totalPages}
						</span>
						<button
							type="button"
							onClick={scrollNext}
							disabled={!canScrollNext}
							className="transition-colors hover:text-neutral-900 disabled:opacity-30"
							aria-label="Next questions"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
								fill="currentColor"
								className="h-4 w-4"
								aria-hidden="true"
							>
								<title>Next</title>
								<path
									fillRule="evenodd"
									d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
					</div>
				)}
			</div>

			<div ref={emblaRef} className="mt-8 overflow-hidden cursor-grab active:cursor-grabbing">
				<div className="-ml-4 flex">
					{items.map((item) => (
						<div
							key={item.question}
							className="min-w-0 shrink-0 basis-[85%] pl-4 sm:basis-[45%] md:basis-1/3 lg:basis-1/4"
						>
							<FaqCard item={item} />
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

export function ProductFaqCarouselSkeleton() {
	return (
		<section className="bg-neutral-50 py-16">
			<div className="container-page">
				<div className="flex items-end justify-between">
					<div>
						<Skeleton className="h-3 w-20" />
						<Skeleton className="mt-2 h-8 w-56" />
					</div>
					<Skeleton className="h-5 w-16" />
				</div>
				<div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{['faq-a', 'faq-b', 'faq-c', 'faq-d'].map((id) => (
						<div key={id} className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
							<Skeleton className="h-4 w-4/5" />
							<Skeleton className="mt-3 h-4 w-full" />
							<Skeleton className="mt-2 h-4 w-5/6" />
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
